import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import useDisclosure from "../../../../../hooks/use-disclosure";
import DocumentPreviewModal from "./DocumentPreviewModal";

interface AttachmentPreviewProps {
  file: File;
  fileType: "file" | "image";
  onRemove: () => void;
  onPreview?: () => void; // Optional callback for preview
}

const AttachmentPreview = ({
  file,
  fileType,
  onRemove,
  onPreview,
}: AttachmentPreviewProps) => {
  const isImage = fileType === "image";
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const previewDisclosure = useDisclosure();

  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file, isImage]);

  const handlePreview = () => {
    if (onPreview) {
      onPreview();
    } else {
      // previewDisclosure.open();
      window.open(fileUrl || URL.createObjectURL(file), "_blank");
    }
  };

  return (
    <>
      <Flex
        alignItems="center"
        gap={1}
        p={0.75}
        sx={{
          bgcolor: "grey.100",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.300",
          maxWidth: 260,
          cursor: "pointer",
          "&:hover": {
            bgcolor: "grey.200",
          },
        }}
        onClick={handlePreview}
      >
        {isImage && fileUrl ? (
          <Box
            component="img"
            src={fileUrl}
            alt={file.name}
            sx={{
              width: 36,
              height: 36,
              objectFit: "cover",
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.200",
              borderRadius: 1,
              flexShrink: 0,
            }}
          >
            <InsertDriveFileIcon sx={{ fontSize: 22, color: "grey.600" }} />
          </Box>
        )}

        <Flex flexDirection="column" flex={1} minWidth={0}>
          <Text
            fontSize="12px"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </Text>
          <Text fontSize="10px" color="text.secondary">
            {(file.size / 1024).toFixed(2)} KB
          </Text>
        </Flex>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            width: 24,
            height: 24,
            color: "text.secondary",
            "&:hover": {
              bgcolor: "grey.300",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Flex>

      <DocumentPreviewModal
        file={file}
        fileName={file.name}
        fileType={fileType}
        disclosureHost={previewDisclosure}
      />
    </>
  );
};

export default AttachmentPreview;
